import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, UserCircle, Tag as TagIcon, X, Plus, Phone, Building2 } from "lucide-react";
import AircallCallButton from "./AircallCallButton";

const STATUS = [
  { k: "actif", l: "Actif", color: "text-emerald-400 bg-emerald-500/15" },
  { k: "premium", l: "Premium", color: "text-amber-300 bg-amber-500/15" },
  { k: "prospect", l: "Prospect", color: "text-sky-300 bg-sky-500/15" },
  { k: "churn", l: "Churn", color: "text-red-400 bg-red-500/15" },
  { k: "inactif", l: "Inactif", color: "text-slate-400 bg-slate-500/15" },
];

/**
 * Compact CRM meta editor: referent commercial, status, tags, multi-societe.
 * Usable on a client or a prospect detail page.
 *
 * Props: ``api`` (axios), ``userId``, ``onChanged`` callback, ``client`` (for phone).
 */
export default function ClientCrmMetaPanel({ api, userId, client, onChanged }) {
  const [meta, setMeta] = useState(null);
  const [staff, setStaff] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTag, setNewTag] = useState("");

  const load = useCallback(async () => {
    try {
      const [m, u, s] = await Promise.all([
        api.get(`/admin/clients/${userId}/meta`),
        api.get("/admin/users").catch(() => ({ data: { users: [] } })),
        api.get("/societes").catch(() => ({ data: [] })),
      ]);
      setMeta(m.data);
      const users = u.data?.users || u.data || [];
      setStaff(users.filter((x) => ["admin", "coach", "sous_traitant", "commercial"].includes(x.role)));
      setSocietes(s.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  }, [api, userId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (data) => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/clients/${userId}/meta`, data);
      setMeta(r.data);
      toast.success("Fiche mise a jour");
      onChanged && onChanged(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSaving(false);
  };

  const addTag = () => {
    if (!newTag.trim()) return;
    const tags = [...(meta?.tags || []), newTag.trim()];
    patch({ tags });
    setNewTag("");
  };
  const removeTag = (t) => patch({ tags: (meta?.tags || []).filter((x) => x !== t) });

  const toggleSociete = (sid) => {
    const current = meta?.societe_ids || [];
    const next = current.includes(sid) ? current.filter((x) => x !== sid) : [...current, sid];
    patch({ societe_ids: next });
  };

  if (loading || !meta) return <div className="text-center py-6"><Loader2 className="w-4 h-4 animate-spin mx-auto text-slate-500" /></div>;

  const currentStatus = STATUS.find((s) => s.k === meta.client_status) || STATUS[0];

  return (
    <div className="bg-[#14101c] border border-white/10 rounded-xl p-4 space-y-4" data-testid="client-crm-meta">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-white flex items-center gap-2"><UserCircle className="w-4 h-4 text-[#f7c935]" /> Fiche CRM</h3>
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
      </div>

      {/* Phone + click-to-call */}
      {client?.phone && (
        <div className="flex items-center gap-2 bg-[#0b0811] border border-white/5 rounded-lg p-2.5">
          <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <span className="text-xs text-slate-300 flex-1">{client.phone}</span>
          <AircallCallButton api={api} prospect={{ prospect_id: userId, phone: client.phone }} onCalled={load} />
        </div>
      )}

      {/* Status */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1.5">Statut client</div>
        <div className="flex flex-wrap gap-1">
          {STATUS.map((s) => (
            <button
              key={s.k}
              onClick={() => meta.client_status !== s.k && patch({ client_status: s.k })}
              className={"text-[10px] px-2.5 py-1 rounded-full font-bold uppercase " + (meta.client_status === s.k ? s.color : "bg-white/5 text-slate-500 hover:text-white")}
              data-testid={"client-status-" + s.k}
            >{s.l}</button>
          ))}
        </div>
      </div>

      {/* Referent commercial */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1.5">Referent commercial</div>
        <select
          value={meta.referent_commercial_user_id || ""}
          onChange={(e) => patch({ referent_commercial_user_id: e.target.value || null })}
          className="w-full bg-[#0b0811] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white"
          data-testid="client-referent-select"
        >
          <option value="">— Aucun —</option>
          {staff.map((u) => (
            <option key={u.user_id} value={u.user_id}>{u.name || u.email} · {u.role}</option>
          ))}
        </select>
        {meta.referent_commercial && (
          <p className="text-[10px] text-slate-500 mt-1">Affecte: {meta.referent_commercial.name || meta.referent_commercial.email}</p>
        )}
      </div>

      {/* Tags */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1.5 flex items-center gap-1"><TagIcon className="w-3 h-3" /> Tags</div>
        <div className="flex flex-wrap gap-1 mb-1.5">
          {(meta.tags || []).map((t) => (
            <span key={t} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#f7c935]/15 text-[#f7c935] font-bold" data-testid={"client-tag-" + t}>
              {t}
              <button onClick={() => removeTag(t)} className="hover:text-red-400"><X className="w-2.5 h-2.5" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-1">
          <input
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addTag()}
            placeholder="Nouveau tag..."
            className="flex-1 bg-[#0b0811] border border-white/10 rounded-lg px-2 py-1 text-[11px] text-white"
            data-testid="client-new-tag-input"
          />
          <button onClick={addTag} className="text-[10px] px-2 rounded bg-[#f7c935]/15 text-[#f7c935] hover:bg-[#f7c935]/25" data-testid="client-add-tag-btn"><Plus className="w-3 h-3" /></button>
        </div>
      </div>

      {/* Multi-societe */}
      {societes.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1.5 flex items-center gap-1"><Building2 className="w-3 h-3" /> Societes rattachees</div>
          <div className="space-y-1">
            {societes.map((s) => {
              const checked = (meta.societe_ids || []).includes(s.societe_id);
              return (
                <label key={s.societe_id} className="flex items-center gap-2 text-xs text-slate-300 hover:bg-white/5 rounded px-2 py-1 cursor-pointer">
                  <input type="checkbox" checked={checked} onChange={() => toggleSociete(s.societe_id)} data-testid={"client-societe-" + s.societe_id} />
                  <span className="flex-1">{s.name}</span>
                  {s.currency && <span className="text-[9px] text-slate-500 uppercase">{s.currency}</span>}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
